import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

import { useStack } from '@/react/common/stacks/queries/useStack';
import { GitStackPayload } from '@/react/common/stacks/types';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useUpdateGitStack } from '@/react/portainer/gitops/queries/useUpdateGitStack';
import { queryKeys } from '@/react/kubernetes/helm/helmReleaseQueries/query-keys';

import { Button } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { HelmRelease } from '../types';

export function SyncWithGitButton({
  stackID,
  release,
  dataCy,
}: {
  stackID: number;
  release: HelmRelease;
  dataCy: string;
}) {
  const { isLoading, mutate } = useSyncWithGitMutation(stackID, release);

  if (isLoading) {
    return null;
  }

  return (
    <Button
      color="none"
      onClick={async () => {
        const confirmed = await confirm({
          title: 'Sync with Git',
          message:
            'This will upgrade the Helm release with the latest changes from the Git repository.',
          confirmButton: buildConfirmButton('Sync'),
          cancelButtonLabel: 'Cancel',
        });
        if (confirmed) {
          mutate();
        }
      }}
      data-cy={dataCy}
      size="xsmall"
    >
      <Badge type={isLoading ? 'muted' : 'warnSecondary'}>
        <Icon icon={RefreshCw} className="!mr-1" />
        Out of sync
      </Badge>
    </Button>
  );
}

function useSyncWithGitMutation(
  stackID: number,
  release: HelmRelease
): {
  isLoading: boolean;
  isError: boolean;
  mutate: () => void;
} {
  const stackQuery = useStack(stackID);
  const environmentId = useEnvironmentId();
  const stack = stackQuery.data;
  const redeployStackMutation = useUpdateGitStack(stackID, environmentId);
  const queryClient = useQueryClient();

  if (!stack) {
    return { isLoading: false, isError: false, mutate: () => {} };
  }
  const payload: GitStackPayload = {
    env: stack.Env,
    RepositoryReferenceName: stack.GitConfig?.ReferenceName,
    RepositoryAuthentication: !!stack.GitConfig?.Authentication,
    RepositoryGitCredentialID: stack.GitConfig?.Authentication?.GitCredentialID,
    RepositoryUsername: stack.GitConfig?.Authentication?.Username,
    RepositoryPassword: stack.GitConfig?.Authentication?.Password,
  };

  return {
    isLoading: redeployStackMutation.isLoading,
    isError: redeployStackMutation.isError,
    mutate: () => {
      redeployStackMutation.mutate(payload); // Optimistically update the query cache to show pending state
      const queryKey = queryKeys.release(
        environmentId,
        release.namespace || '',
        release.name,
        release.version,
        true
      );
      // there's a delay upgrading, so optimistically update the release to show a pending state
      queryClient.setQueryData(queryKey, {
        ...release,
        info: {
          ...release.info,
          status: 'pending-upgrade',
          details: 'Upgrade in progress...',
          description: 'Upgrade in progress...',
        },
      });
    },
  };
}
